import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useAnimatedNumber } from "@/lib/useAnimatedNumber";
import ExecutiveBrief from "@/components/ExecutiveBrief";
import MonthOnMonthChart from "@/components/MonthOnMonthChart";
import PendingSinceTable from "@/components/PendingSinceTable";
import { ClipboardText, CheckCircle, Clock, Warning, Buildings, ChartLineUp, ArrowRight } from "@phosphor-icons/react";

function KPI({ label, value, tone = "blue", suffix = "", icon, testId, decimals = 0 }) {
  const v = useAnimatedNumber(value || 0);
  const display = decimals ? v.toFixed(decimals) : Math.round(v);
  const tones = {
    blue: "bg-[#0B2A5B] text-white",
    red: "bg-red-50 text-red-800 border border-red-200",
    amber: "bg-amber-50 text-amber-800 border border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border border-emerald-200",
    slate: "bg-slate-50 text-slate-800 border border-slate-200",
  };
  return (
    <div className={`rounded-md p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-75 mb-1">
        {icon} {label}
      </div>
      <div className="text-3xl font-semibold tabular-nums" style={{ fontFamily: "Geist" }}>
        {display}{suffix}
      </div>
    </div>
  );
}

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await api.get(`/analytics/partner-overview${refresh ? "?refresh=true" : ""}`);
      setData(r.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const totals = data?.totals || {};
  const clients = data?.clients || [];

  const header = (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Geist" }}>Partner's View</h1>
      <p className="text-sm text-slate-500 mt-0.5">
        Firm-wide engagement health across all clients — trends, theme concentration, and oldest unresolved items.
      </p>
    </div>
  );

  if (loading) {
    return (
      <AppLayout header={header}>
        <div className="text-sm text-slate-500" data-testid="partner-loading">Loading firm-wide overview...</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout header={header}>
      <div className="space-y-6" data-testid="partner-dashboard">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Clients" value={clients.length} tone="blue" icon={<Buildings size={11} />} testId="kpi-clients" />
          <KPI label="Open Observations" value={totals.open || 0} tone="slate" icon={<ClipboardText size={11} />} testId="kpi-open" />
          <KPI label="High Risk Open" value={totals.high_open || 0} tone="red" icon={<Warning size={11} />} testId="kpi-high" />
          <KPI label="Resolution Rate" value={totals.resolution_rate || 0} suffix="%" tone="emerald" icon={<CheckCircle size={11} />} testId="kpi-resolution-rate" decimals={1} />
          <KPI label="Avg Resolution Days" value={totals.avg_resolution_days || 0} tone="amber" icon={<Clock size={11} />} testId="kpi-avg-days" decimals={1} />
        </div>

        <ExecutiveBrief
          brief={data?.ai_brief}
          audience="Partner"
          onRefresh={() => load(true)}
          refreshing={refreshing}
          cached={data?._cached}
        />

        <div>
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-800">
            <ChartLineUp size={14} /> Firm-wide Engagement (last 12 months)
          </div>
          <MonthOnMonthChart series={data?.month_series || []} />
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="clients-overview">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-800">Clients · Sorted by High-Risk Exposure</h4>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Click to drill in</span>
          </div>
          {clients.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No clients yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clients.map((c) => (
                <Link
                  to={`/audit/companies/${c.id}`}
                  key={c.id}
                  className="rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 p-3 transition lift-on-hover"
                  data-testid={`partner-client-${c.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-800 truncate flex items-center gap-1.5">
                      <Buildings size={13} /> {c.name}
                    </div>
                    <ArrowRight size={14} className="text-slate-400" />
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{c.total} total</span>
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{c.open} open</span>
                    {c.high > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold">
                        {c.high} high
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <PendingSinceTable items={data?.pending_since || []} showCompany={false} />
      </div>
    </AppLayout>
  );
}
